/**
 * FerpaSourceVerifications
 *
 * Bot-grouped FERPA source verification UI — mirrors the HIPAA Compliance Center
 * architecture exactly (bot cards → tabs → source cards → details modal → evidence).
 *
 * Architecture:
 *   BotGroupCard (collapsed by default)
 *     └─ Tabs: Knowledge Sources | FERPA Control Modules | Verification Details
 *         └─ SourceCard  →  [View Details] opens FerpaVerificationModal
 *
 * All APIs use the /api/organization/compliance/ferpa/* namespace.
 * Zero shared state or tables with HIPAA.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2, XCircle, Clock, RefreshCcw, ChevronDown, ChevronUp, ChevronRight,
  Link2, ExternalLink, ShieldCheck, ShieldAlert, ShieldOff, AlertTriangle,
  History, Wrench, FileText, Upload, Download, GraduationCap, Trash2,
  List, ClipboardList,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ── Auth helper (hook pattern — matches HIPAA exactly) ────────────────────────
function useFerpaFetch() {
  return (url: string, opts: RequestInit = {}) => {
    const token =
      sessionStorage.getItem("dotnetbot.jwt.session") ||
      localStorage.getItem("dotnetbot.jwt") ||
      "";
    return fetch(url, {
      ...opts,
      headers: {
        ...(opts.headers as Record<string, string> || {}),
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  };
}

function useCurrentUserInfo(): { email: string; displayName: string } {
  try {
    const token =
      sessionStorage.getItem("dotnetbot.jwt.session") ||
      localStorage.getItem("dotnetbot.jwt") ||
      "";
    if (!token) return { email: "", displayName: "" };
    const payload = JSON.parse(atob(token.split(".")[1]));
    const email =
      payload.email ||
      payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"] ||
      "";
    const displayName = payload.username || payload.name || email.split("@")[0] || "";
    return { email, displayName };
  } catch {
    return { email: "", displayName: "" };
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface FerpaControlResult {
  id: string;
  controlKey: string;
  controlName: string;
  verificationMethod: "automatic" | "manual_required" | string;
  complianceStatus: "passed" | "failed" | "manual_required" | "not_applicable" | string;
  isRequired: boolean;
  findings: string | null;
  evidenceRequired: string | null;
  evidenceReference: string | null;
  verifiedAt: string | null;
  linkedEntityId: string | null;
  linkedEntityType: string | null;
}

export interface FerpaVerification {
  id: string;
  knowledgeSourceId: string;
  organizationId: string;
  chatbotId: string;
  sourceName: string;
  sourceType: string;
  overallStatus: "pending_verification" | "active" | "blocked" | "pending_review" | "allowed" | string;
  verificationStartedAt: string | null;
  verificationCompletedAt: string | null;
  verificationSummary: string | null;
  passedCount: number;
  failedCount: number;
  manualRequiredCount: number;
  createdAt: string;
  updatedAt: string;
  controlResults: FerpaControlResult[];
}

interface FerpaDecision {
  id: string;
  // Backend-canonical values (see FerpaController.CreateFerpaDecision validTypes):
  decisionType: "reverification_required" | "risk_accepted" | "exception_approved" | "blocked" | "allowed" | string;
  justification: string | null;
  approverName: string | null;
  approverTitle: string | null;
  approvalDate: string | null;
  expirationDate: string | null;
  previousStatus: string;
  newStatus: string;
  createdByEmail: string;
  createdAt: string;
  isExpired?: boolean;
  expiredAt?: string | null;
}

type DecisionModal = "fix_required" | "accept_risk" | "exception_approved" | "keep_blocked" | null;
type BotTab = "sources" | "controls" | "details";

// ── FERPA control reference labels ────────────────────────────────────────────
// Keys must match the LinkedEntityType values the FerpaVerificationWorker assigns
// via ControlEntityTypeMap in FerpaVerificationQueue.cs.
const FERPA_ENTITY_TYPE_LABELS: Record<string, string> = {
  // ── Values actually written by the backend worker ────────────────────────
  FerpaVendorAgreement:             "Legitimate Educational Interest",
  FerpaConsentRecord:               "Consent Records",
  FerpaDirectoryInfoPolicy:         "Directory Information Policy",
  FerpaDataMinimization:            "Data Minimization / Minimum Necessary Access",
  FerpaDisclosure:                  "Disclosure Accounting",
  FerpaAnnualNotice:                "Annual FERPA Rights Notice",
  FerpaStudentAccessRequest:        "Student Inspection & Amendment Rights",
  FerpaRetentionPolicy:             "Data Retention Policy",
  FerpaRolePermissionMapping:       "Access Control",
  FerpaTransmissionSecurityControl: "Secure Transmission (HTTPS)",
  FerpaBrowserStorageControl:       "Browser Cache & Session Storage Protection",
  // ── Legacy keys kept for backward compatibility ──────────────────────────
  FerpaLeiPolicy:                   "Legitimate Educational Interest",
  FerpaMinimumNecessary:            "Data Minimization / Minimum Necessary Access",
  FerpaDisclosureLog:               "Disclosure Accounting",
  FerpaStudentRights:               "Student Inspection & Amendment Rights",
  FerpaAccessControl:               "Access Control",
};

// Fallback category map for automatic controls whose LinkedEntityType is null.
// The backend AutoLinkFerpaEntitiesAsync only links manual controls, so automatic
// controls (https_required, no_store_cache) always arrive with linkedEntityType = null.
const FERPA_CONTROL_KEY_CATEGORY: Record<string, string> = {
  https_required:                  "Secure Transmission (HTTPS)",
  no_store_cache:                  "Browser Cache & Session Storage Protection",
  // Manual keys — used if entity-link hasn't been written yet
  legitimate_educational_interest: "Legitimate Educational Interest",
  consent_verification:            "Consent Records",
  directory_info_policy:           "Directory Information Policy",
  minimum_necessary:               "Data Minimization / Minimum Necessary Access",
  disclosure_accounting:           "Disclosure Accounting",
  annual_notice:                   "Annual FERPA Rights Notice",
  student_rights:                  "Student Inspection & Amendment Rights",
  data_retention:                  "Data Retention Policy",
  access_control:                  "Access Control",
};

// ── Status helpers ────────────────────────────────────────────────────────────
function statusBadge(status: string) {
  if (status === "active")               return <Badge variant="default"    className="text-xs">✅ Passed</Badge>;
  if (status === "blocked")              return <Badge variant="destructive" className="text-xs">🔴 Blocked</Badge>;
  if (status === "allowed")              return <Badge className="text-xs bg-amber-600 hover:bg-amber-600">🟡 Risk Accepted</Badge>;
  if (status === "pending_review")       return <Badge variant="secondary"  className="text-xs">⏳ Pending Review</Badge>;
  if (status === "pending_verification") return <Badge variant="secondary"  className="text-xs">⏳ Verifying…</Badge>;
  return <Badge variant="outline" className="text-xs">{status}</Badge>;
}

function decisionTypeLabel(type: string) {
  // Backend-canonical values
  if (type === "risk_accepted")           return "Risk Accepted";
  if (type === "exception_approved")      return "Exception Approved";
  if (type === "reverification_required") return "Fix Required";
  if (type === "blocked")                 return "Kept Blocked";
  if (type === "allowed")                 return "Allowed";
  // Legacy values stored before the mapping was corrected
  if (type === "accept_risk")             return "Risk Accepted";
  if (type === "remediation_required")    return "Fix Required";
  if (type === "keep_blocked")            return "Kept Blocked";
  return type;
}

function decisionTypeBadge(type: string, expired?: boolean) {
  // Backend-canonical values
  if (type === "risk_accepted")
    return <Badge className="text-xs bg-amber-600 hover:bg-amber-600">🟡 Risk Accepted</Badge>;
  if (type === "exception_approved")
    return expired
      ? <Badge variant="destructive" className="text-xs">Exception Expired</Badge>
      : <Badge className="text-xs bg-emerald-600 hover:bg-emerald-600">🟢 Exception Approved</Badge>;
  if (type === "reverification_required")
    return <Badge variant="destructive" className="text-xs">Fix Required</Badge>;
  if (type === "blocked")
    return <Badge variant="outline" className="text-xs">🔴 Kept Blocked</Badge>;
  if (type === "allowed")
    return <Badge className="text-xs bg-emerald-600 hover:bg-emerald-600">✅ Allowed</Badge>;
  // Legacy values stored before the mapping was corrected
  if (type === "accept_risk")
    return <Badge className="text-xs bg-amber-600 hover:bg-amber-600">🟡 Risk Accepted</Badge>;
  if (type === "remediation_required")
    return <Badge variant="destructive" className="text-xs">Fix Required</Badge>;
  if (type === "keep_blocked")
    return <Badge variant="outline" className="text-xs">🔴 Kept Blocked</Badge>;
  return <Badge variant="outline" className="text-xs">{type}</Badge>;
}

function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

// ── Evidence types ────────────────────────────────────────────────────────────
const FERPA_EVIDENCE_TYPES = [
  { value: "policy_document",   label: "Policy Document" },
  { value: "consent_form",      label: "Consent Form" },
  { value: "procedure",         label: "Procedure / SOP" },
  { value: "training_record",   label: "Training Record" },
  { value: "audit_log",         label: "Audit Log Excerpt" },
  { value: "disclosure_log",    label: "Disclosure Accounting Log" },
  { value: "legal_agreement",   label: "Legal Agreement" },
  { value: "screenshot",        label: "Screenshot / System Evidence" },
  { value: "assessment_report", label: "Assessment Report" },
  { value: "other",             label: "Other" },
] as const;

// ── FERPA-specific evidence placeholders per control key ──────────────────────
const FERPA_CONTROL_PLACEHOLDERS: Record<string, { placeholder: string; hint: string }> = {
  legitimate_educational_interest: {
    placeholder: "Describe the LEI policy governing who may access student records from this source.",
    hint: "e.g. Policy defines school official roles, responsibilities, and the LEI criteria for each data access purpose.",
  },
  consent_verification: {
    placeholder: "Describe how student/parent consent is obtained and tracked for disclosures from this source.",
    hint: "e.g. Written consent forms are collected before any third-party disclosure; records retained for 5 years.",
  },
  directory_info_policy: {
    placeholder: "Describe the directory information opt-out policy and how it applies to this source.",
    hint: "e.g. Annual notice published; opt-out requests tracked in SIS; directory fields restricted accordingly.",
  },
  minimum_necessary: {
    placeholder: "Describe how access from this source is limited to the minimum fields necessary.",
    hint: "e.g. Role-based filters expose only name and enrollment status; SSN and financial data excluded.",
  },
  disclosure_accounting: {
    placeholder: "Describe how disclosures from this source are logged in the FERPA disclosure accounting log.",
    hint: "e.g. Every third-party disclosure is recorded in the disclosure log with date, recipient, and purpose.",
  },
  annual_notice: {
    placeholder: "Confirm annual FERPA rights notices reference this source and attach the current notice.",
    hint: "e.g. Annual notice published in student handbook and institutional website; includes all data categories.",
  },
  student_rights: {
    placeholder: "Describe the procedure allowing students to inspect, amend, or challenge records from this source.",
    hint: "e.g. Student inspection requests processed within 45 days; amendment process documented.",
  },
  data_retention: {
    placeholder: "Describe the records retention schedule for data from this source.",
    hint: "e.g. Records retained per FERPA §99.32 for 5 years after graduation; disposition schedule enforced.",
  },
  access_control: {
    placeholder: "Describe the access control mechanism limiting this source to authorized school officials only.",
    hint: "e.g. Role-based access controls; periodic access reviews; MFA enforced for all users.",
  },
};

const FERPA_DEFAULT_PLACEHOLDER = {
  placeholder: "Describe the evidence, policy, or control measure supporting FERPA compliance for this check.",
  hint: "e.g. Policy reviewed and approved; procedure implemented and tested.",
};

// ── File attachment helpers ────────────────────────────────────────────────────
const ALLOWED_ATTACHMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
];
const ALLOWED_ATTACHMENT_EXT = ".pdf,.doc,.docx,.png,.jpg,.jpeg";
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "📄";
  if (ext === "doc" || ext === "docx") return "📝";
  if (ext === "png" || ext === "jpg" || ext === "jpeg") return "🖼";
  return "📎";
}

interface AttachmentData { name: string; size: number; dataUrl: string; }
interface EvidenceData {
  title: string; type: string; description: string;
  reference: string; attachment?: AttachmentData;
  addedBy: string; addedAt: string;
}

function parseEvidence(raw: string | null): EvidenceData | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.title) return parsed as EvidenceData;
  } catch { /* legacy plain-text fallback */ }
  return null;
}

function evidenceTypeLabel(type: string) {
  return FERPA_EVIDENCE_TYPES.find(t => t.value === type)?.label ?? type;
}

// ── Evidence edit form ────────────────────────────────────────────────────────
function EvidenceForm({
  sourceId, controlKey, currentEvidence, onSaved, onCancel,
}: {
  sourceId: string; controlKey: string;
  currentEvidence: string | null; onSaved: () => void; onCancel: () => void;
}) {
  const authFetch = useFerpaFetch();
  const { email } = useCurrentUserInfo();
  const { toast }  = useToast();
  const [saving, setSaving] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const parsed = parseEvidence(currentEvidence);
  const [title,       setTitle]       = useState(parsed?.title       ?? "");
  const [type,        setType]        = useState(parsed?.type        ?? "");
  const [description, setDescription] = useState(parsed?.description ?? "");
  const [reference,   setReference]   = useState(parsed?.reference   ?? "");
  const [attachment,  setAttachment]  = useState<AttachmentData | null>(parsed?.attachment ?? null);

  const { placeholder, hint } = FERPA_CONTROL_PLACEHOLDERS[controlKey] ?? FERPA_DEFAULT_PLACEHOLDER;
  const canSave = title.trim().length > 0 && type.length > 0;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      toast({ title: "Unsupported file type. Use PDF, DOC, DOCX, PNG, or JPG.", variant: "destructive" });
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast({ title: `File too large. Maximum is ${formatBytes(MAX_ATTACHMENT_BYTES)}.`, variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => setAttachment({ name: file.name, size: file.size, dataUrl: ev.target!.result as string });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSaving(true);
    try {
      const payload: EvidenceData = {
        title: title.trim(), type,
        description: description.trim(), reference: reference.trim(),
        ...(attachment ? { attachment } : {}),
        addedBy: email || "—", addedAt: new Date().toISOString(),
      };
      const r = await authFetch(
        `/api/organization/compliance/ferpa/source-verifications/${sourceId}/controls/${controlKey}`,
        { method: "PATCH", body: JSON.stringify({ evidenceReference: JSON.stringify(payload) }) },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast({ title: (err as any).message ?? "Failed to save evidence.", variant: "destructive" });
      } else {
        toast({ title: "Evidence saved — control marked PASSED." });
        onSaved();
      }
    } catch {
      toast({ title: "Network error saving evidence.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded border border-dashed border-purple-300 bg-purple-50/50 p-3 space-y-3"
      onClick={e => e.stopPropagation()}
    >
      <p className="text-xs font-semibold text-purple-800 flex items-center gap-1.5">
        <FileText className="w-3.5 h-3.5" aria-hidden="true" />
        {currentEvidence ? "Edit Evidence" : "Add Evidence"}
      </p>

      {/* Title */}
      <div>
        <Label className="text-xs font-medium">Evidence Title <span className="text-red-500">*</span></Label>
        <Input className="mt-0.5 h-7 text-xs" placeholder="e.g. FERPA LEI Policy v3"
          value={title} onChange={e => setTitle(e.target.value)} maxLength={200}
          onClick={e => e.stopPropagation()} />
      </div>

      {/* Type */}
      <div>
        <Label className="text-xs font-medium">Evidence Type <span className="text-red-500">*</span></Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="mt-0.5 h-7 text-xs" onClick={e => e.stopPropagation()}>
            <SelectValue placeholder="Select type…" />
          </SelectTrigger>
          <SelectContent>
            {FERPA_EVIDENCE_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Reference + file */}
      <div className="space-y-2">
        <Label className="text-xs font-medium">Evidence Reference</Label>
        <div>
          <p className="text-[10px] text-muted-foreground mb-0.5">URL / Document Reference</p>
          <Input className="h-7 text-xs font-mono" placeholder="Enter URL or document reference"
            value={reference} onChange={e => setReference(e.target.value)} maxLength={1000}
            onClick={e => e.stopPropagation()} />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 border-t border-muted" />
          <span className="text-[10px] text-muted-foreground font-medium">OR</span>
          <div className="flex-1 border-t border-muted" />
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground mb-1">Attachment</p>
          {attachment ? (
            <div className="flex items-center gap-2 rounded border border-purple-200 bg-white px-2.5 py-1.5 text-xs">
              <span className="text-base leading-none" aria-hidden="true">{fileIcon(attachment.name)}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{attachment.name}</p>
                <p className="text-muted-foreground text-[10px]">{formatBytes(attachment.size)}</p>
              </div>
              <Button size="sm" variant="ghost"
                className="h-6 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-1.5 flex-shrink-0"
                onClick={e => { e.stopPropagation(); setAttachment(null); }}>
                Remove
              </Button>
            </div>
          ) : (
            <button type="button"
              className="w-full flex items-center justify-center gap-2 rounded border border-dashed border-muted-foreground/40 bg-white hover:border-purple-400 hover:bg-purple-50/30 transition-colors px-3 py-2 text-xs text-muted-foreground hover:text-purple-700"
              onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>
              <Upload className="w-3.5 h-3.5" aria-hidden="true" />
              <span>Upload File</span>
            </button>
          )}
          <input ref={fileInputRef} type="file" accept={ALLOWED_ATTACHMENT_EXT}
            className="hidden" onChange={handleFileChange}
            onClick={e => e.stopPropagation()} />
          {!attachment && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Supported: PDF, DOC, DOCX, PNG, JPG · Max {formatBytes(MAX_ATTACHMENT_BYTES)}
            </p>
          )}
        </div>
      </div>

      {/* Description */}
      <div>
        <Label className="text-xs font-medium">Description / Notes</Label>
        <Textarea className="mt-0.5 text-xs" rows={3} placeholder={placeholder}
          value={description} onChange={e => setDescription(e.target.value)} maxLength={2000}
          onClick={e => e.stopPropagation()} />
        {!description && hint && (
          <p className="text-[10px] text-muted-foreground/70 mt-0.5 italic">{hint}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs px-3 bg-purple-600 hover:bg-purple-700"
          onClick={handleSave} disabled={saving || !canSave}>
          {saving ? "Saving…" : "Save Evidence"}
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-xs"
          onClick={e => { e.stopPropagation(); onCancel(); }} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Saved evidence read-only card ─────────────────────────────────────────────
function EvidenceCard({
  currentEvidence, onEdit, canManage,
}: {
  currentEvidence: string; onEdit: () => void; canManage: boolean;
}) {
  const parsed = parseEvidence(currentEvidence);
  const isUrl  = (s: string) => /^https?:\/\//.test(s);

  if (!parsed) {
    return (
      <div className="rounded border bg-muted/30 px-2.5 py-2 text-xs" onClick={e => e.stopPropagation()}>
        <p className="font-medium mb-0.5">Evidence:</p>
        <p className="text-muted-foreground break-words">{currentEvidence}</p>
        {canManage && (
          <Button size="sm" variant="ghost" className="mt-1 h-6 text-xs px-1.5"
            onClick={e => { e.stopPropagation(); onEdit(); }}>Edit</Button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded border border-purple-200 bg-purple-50/40 px-2.5 py-2.5 text-xs space-y-2"
      onClick={e => e.stopPropagation()}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-purple-900 flex items-center gap-1.5">
          <FileText className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
          Evidence
        </p>
        {canManage && (
          <Button size="sm" variant="ghost"
            className="h-6 text-xs px-1.5 flex-shrink-0 text-purple-700 hover:text-purple-900"
            onClick={e => { e.stopPropagation(); onEdit(); }}>
            Edit Evidence
          </Button>
        )}
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
        {parsed.title && <>
          <span className="font-bold text-foreground whitespace-nowrap">Title:</span>
          <span className="text-foreground break-words">{parsed.title}</span>
        </>}
        {parsed.type && <>
          <span className="font-bold text-foreground whitespace-nowrap">Type:</span>
          <span className="text-foreground">{evidenceTypeLabel(parsed.type)}</span>
        </>}
        {parsed.description && <>
          <span className="font-bold text-foreground whitespace-nowrap">Description:</span>
          <span className="text-foreground break-words">{parsed.description}</span>
        </>}
        {parsed.reference && <>
          <span className="font-bold text-foreground whitespace-nowrap">Reference:</span>
          {isUrl(parsed.reference) ? (
            <a href={parsed.reference} target="_blank" rel="noopener noreferrer"
              className="text-purple-700 underline break-all flex items-center gap-1"
              onClick={e => e.stopPropagation()}>
              {parsed.reference}
              <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" aria-hidden="true" />
            </a>
          ) : (
            <span className="text-foreground break-words">{parsed.reference}</span>
          )}
        </>}
        {parsed.attachment && <>
          <span className="font-bold text-foreground whitespace-nowrap col-span-2 mt-1 border-t border-purple-100 pt-2">
            Attachment
          </span>
          <div className="col-span-2 rounded border border-purple-100 bg-white/60 px-2.5 py-2 flex flex-col gap-1.5"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-1.5">
              <span className="text-base leading-none flex-shrink-0" aria-hidden="true">
                {fileIcon(parsed.attachment.name)}
              </span>
              <span className="font-medium text-foreground truncate">{parsed.attachment.name}</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <span className="font-bold text-foreground">Size:</span>
              <span>{formatBytes(parsed.attachment.size)}</span>
            </div>
            <div className="flex items-center gap-2 pt-0.5">
              <a href={parsed.attachment.dataUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded border border-purple-300 bg-purple-50 px-2 py-0.5 text-purple-700 hover:bg-purple-100 transition-colors"
                onClick={e => e.stopPropagation()}>
                <ExternalLink className="w-3 h-3" aria-hidden="true" />View
              </a>
              <a href={parsed.attachment.dataUrl} download={parsed.attachment.name}
                className="inline-flex items-center gap-1 rounded border border-slate-300 bg-slate-50 px-2 py-0.5 text-slate-700 hover:bg-slate-100 transition-colors"
                onClick={e => e.stopPropagation()}>
                <Download className="w-3 h-3" aria-hidden="true" />Download
              </a>
            </div>
          </div>
        </>}
        {parsed.addedBy && <>
          <span className="font-bold text-foreground whitespace-nowrap">Added By:</span>
          <span className="text-foreground break-words">{parsed.addedBy}</span>
        </>}
        {parsed.addedAt && <>
          <span className="font-bold text-foreground whitespace-nowrap">Added Date:</span>
          <span className="text-foreground">{fmt(parsed.addedAt)}</span>
        </>}
      </div>
    </div>
  );
}

// ── Evidence section — collapsible with independent edit state ────────────────
function EvidenceSection({
  sourceId, controlKey, currentEvidence, canManage, onSaved,
}: {
  sourceId: string; controlKey: string; currentEvidence: string | null;
  canManage: boolean; onSaved: () => void;
}) {
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(false);

  return (
    <div onClick={e => e.stopPropagation()}>
      <button type="button"
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors py-1 w-full text-left"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); if (open) setEditing(false); }}>
        {open
          ? <ChevronDown  className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
          : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />}
        Evidence
        {currentEvidence && !open && (
          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700">
            Saved
          </span>
        )}
        {!currentEvidence && canManage && !open && (
          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
            Required
          </span>
        )}
      </button>

      {open && (
        <div className="mt-1 space-y-2" onClick={e => e.stopPropagation()}>
          {currentEvidence && !editing && (
            <EvidenceCard currentEvidence={currentEvidence}
              onEdit={() => setEditing(true)} canManage={canManage} />
          )}
          {canManage && (editing || !currentEvidence) && (
            <EvidenceForm sourceId={sourceId} controlKey={controlKey}
              currentEvidence={currentEvidence}
              onSaved={() => { setEditing(false); onSaved(); }}
              onCancel={editing ? () => setEditing(false) : () => setOpen(false)} />
          )}
          {!canManage && !currentEvidence && (
            <p className="text-xs text-muted-foreground italic py-1">No evidence submitted.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── FERPA compliance control card ─────────────────────────────────────────────
function ControlComplianceCard({
  r, sourceId, canManage, onEvidenceSaved,
}: {
  r: FerpaControlResult; sourceId: string; canManage: boolean; onEvidenceSaved: () => void;
}) {
  const passed        = r.complianceStatus === "passed";
  const failed        = r.complianceStatus === "failed";
  const notApplicable = r.complianceStatus === "not_applicable";
  const isManual      = r.verificationMethod === "manual_required";
  const needsEvidence = r.complianceStatus === "manual_required";
  const linkedLabel   = r.linkedEntityType
    ? (FERPA_ENTITY_TYPE_LABELS[r.linkedEntityType] ?? r.linkedEntityType)
    : null;

  const cardBorder =
    failed        ? "border-red-200 bg-red-50/40" :
    needsEvidence ? "border-amber-200 bg-amber-50/40" :
    passed        ? "border-green-100 bg-green-50/20" :
                    "border-muted bg-muted/10";

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${cardBorder}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {passed        && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" aria-hidden="true" />}
          {failed        && <XCircle      className="w-4 h-4 text-red-600 flex-shrink-0"   aria-hidden="true" />}
          {needsEvidence && <Clock        className="w-4 h-4 text-amber-500 flex-shrink-0" aria-hidden="true" />}
          {notApplicable && <CheckCircle2 className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />}
          <span className={`font-semibold text-xs ${failed ? "text-red-800" : needsEvidence ? "text-amber-800" : ""}`}>
            {r.controlName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {passed        && <Badge className="text-[10px] h-5 px-1.5 bg-green-600 hover:bg-green-600">PASSED</Badge>}
          {failed        && <Badge variant="destructive" className="text-[10px] h-5 px-1.5">FAILED</Badge>}
          {needsEvidence && <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-amber-100 text-amber-800 hover:bg-amber-100">PENDING</Badge>}
          {notApplicable && <Badge variant="outline" className="text-[10px] h-5 px-1.5">N/A</Badge>}
          <Badge variant="outline"
            className={`text-[10px] h-5 px-1.5 ${isManual ? "border-purple-300 text-purple-700" : "border-muted text-muted-foreground"}`}>
            {isManual ? "Manual" : "Automatic"}
          </Badge>
        </div>
      </div>

      {/* Finding details */}
      {failed && r.findings && (
        <div className="rounded bg-red-50 border border-red-200 px-2.5 py-1.5 text-xs">
          <p className="font-medium text-red-800 mb-0.5">Finding:</p>
          <p className="text-red-700">{r.findings}</p>
        </div>
      )}

      {/* Evidence required */}
      {needsEvidence && r.evidenceRequired && (
        <div className="rounded bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-xs text-amber-800">
          <span className="font-medium">Required: </span>{r.evidenceRequired}
        </div>
      )}

      {/* Type + Control reference — labeled rows */}
      <div className="space-y-0.5 text-xs">
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Type:</span>{" "}
          {isManual ? "Manual" : "Automatic"}
        </p>
        <p className="flex items-baseline gap-1 text-purple-700 flex-wrap">
          <span className="font-medium text-foreground whitespace-nowrap">FERPA Control Reference:</span>
          <span>{linkedLabel ?? r.controlKey}</span>
        </p>
      </div>

      {/* Evidence section (manual controls) */}
      {isManual && (
        <EvidenceSection sourceId={sourceId} controlKey={r.controlKey}
          currentEvidence={r.evidenceReference}
          canManage={canManage} onSaved={onEvidenceSaved} />
      )}
    </div>
  );
}

// ── Decision history ──────────────────────────────────────────────────────────
function DecisionHistoryPanel({ verificationId }: { verificationId: string }) {
  const authFetch = useFerpaFetch();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery<FerpaDecision[]>({
    queryKey: ["ferpa-decisions", verificationId],
    queryFn: async () => {
      const r = await authFetch(
        `/api/organization/compliance/ferpa/source-verifications/${verificationId}/decisions`,
      );
      if (!r.ok) return [];
      return r.json();
    },
    staleTime: 10_000, enabled: open,
  });

  const count = data?.length ?? 0;

  return (
    <div className="mt-2">
      <button type="button"
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setOpen(v => !v)} aria-expanded={open}>
        <History className="w-3.5 h-3.5" aria-hidden="true" />
        Decision History{count > 0 ? ` (${count})` : ""}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && isLoading && <Skeleton className="h-12 w-full mt-2" />}

      {open && !isLoading && data && data.length === 0 && (
        <p className="text-xs text-muted-foreground mt-1 ml-5">No decisions recorded yet.</p>
      )}

      {open && data && data.length > 0 && (
        <div className="mt-2 rounded border bg-background divide-y">
          {data.map(d => (
            <div key={d.id} className="px-3 py-2 text-xs space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                {decisionTypeBadge(d.decisionType, d.isExpired)}
                <span className="text-muted-foreground text-[10px]">
                  {new Date(d.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                </span>
                {d.isExpired && d.expiredAt && (
                  <span className="text-red-600 text-[10px]">
                    Expired {new Date(d.expiredAt).toLocaleDateString()}
                  </span>
                )}
              </div>
              {d.approverName && (
                <p className="text-muted-foreground">
                  Approved by <span className="font-medium">{d.approverName}</span>
                  {d.approverTitle ? ` (${d.approverTitle})` : ""}
                  {d.approvalDate ? ` on ${fmt(d.approvalDate)}` : ""}
                  {d.expirationDate ? ` · expires ${fmt(d.expirationDate)}` : ""}
                </p>
              )}
              {d.justification && (
                <p className="text-muted-foreground italic">"{d.justification}"</p>
              )}
              <p className="text-muted-foreground text-[10px]">By: {d.createdByEmail}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Justification parser ──────────────────────────────────────────────────────
interface ParsedJustification {
  justification: string; businessReason: string;
  evidenceRef: string; reviewDate: string; validUntil: string;
}

function parseJustification(raw: string | null): ParsedJustification {
  if (!raw) return { justification: "", businessReason: "", evidenceRef: "", reviewDate: "", validUntil: "" };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && ("justification" in parsed || "businessReason" in parsed)) {
      return {
        justification: parsed.justification ?? "",
        businessReason: parsed.businessReason ?? "",
        evidenceRef: parsed.evidenceRef ?? "",
        reviewDate: parsed.reviewDate ?? "",
        validUntil: parsed.validUntil ?? "",
      };
    }
  } catch { /* legacy */ }
  const brMatch = raw.match(/\n\nBusiness reason:\s*([\s\S]*?)(?:\n\nEvidence:|$)/);
  const evMatch = raw.match(/\n\nEvidence:\s*([\s\S]*)$/);
  return {
    justification: raw.split("\n\nBusiness reason:")[0]?.trim() ?? raw,
    businessReason: brMatch?.[1]?.trim() ?? "",
    evidenceRef: evMatch?.[1]?.trim() ?? "",
    reviewDate: "", validUntil: "",
  };
}

// ── Current decision display ──────────────────────────────────────────────────
function CurrentDecisionPanel({ verificationId }: { verificationId: string }) {
  const authFetch = useFerpaFetch();
  const { data } = useQuery<FerpaDecision[]>({
    queryKey: ["ferpa-decisions", verificationId],
    queryFn: async () => {
      const r = await authFetch(
        `/api/organization/compliance/ferpa/source-verifications/${verificationId}/decisions`,
      );
      if (!r.ok) return [];
      return r.json();
    },
    staleTime: 10_000,
  });

  const latest = data?.[0];
  if (!latest) return null;

  const dt = latest.decisionType;
  const expired = latest.isExpired;

  if (dt === "risk_accepted" || dt === "accept_risk") {
    const { justification, businessReason, reviewDate, validUntil } = parseJustification(latest.justification);
    const displayValidUntil = validUntil || latest.expirationDate || "";
    return (
      <div className="rounded border border-amber-300 bg-amber-50 px-3 py-3 text-xs space-y-2">
        <p className="font-semibold text-amber-800 flex items-center gap-1.5 text-sm">
          <ShieldAlert className="w-4 h-4" aria-hidden="true" />Compliance Decision: Risk Accepted
        </p>
        <p className="text-amber-700 text-[11px]">
          This control has an unresolved finding. The risk has been reviewed and accepted by an authorized compliance user.
        </p>
        <div className="border-t border-amber-200 pt-2 space-y-1">
          {latest.approverName && (
            <p className="text-amber-700">Approved By: <span className="font-medium">{latest.approverName}</span></p>
          )}
          <p className="text-amber-700">Approval Date: <span className="font-medium">{fmt(latest.approvalDate ?? latest.createdAt)}</span></p>
          {reviewDate && (
            <p className="text-amber-700">Next Review Date: <span className="font-medium">{fmt(reviewDate)}</span></p>
          )}
          {displayValidUntil && (
            <p className="text-amber-700">Risk Acceptance Valid Until: <span className="font-medium">{fmt(displayValidUntil)}</span></p>
          )}
          {businessReason && <p className="text-amber-700">Business Reason: <span className="italic">{businessReason}</span></p>}
          {justification   && <p className="text-amber-700">Justification: <span className="italic">{justification}</span></p>}
        </div>
      </div>
    );
  }

  if (dt === "exception_approved") {
    const { justification } = parseJustification(latest.justification);
    const color = expired ? "red" : "emerald";
    return (
      <div className={`rounded border px-3 py-3 text-xs space-y-2 ${expired ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"}`}>
        <p className={`font-semibold flex items-center gap-1.5 text-sm ${expired ? "text-red-800" : "text-emerald-800"}`}>
          <ShieldCheck className="w-4 h-4" aria-hidden="true" />
          Compliance Decision: {expired ? "Exception Expired" : "Exception Approved"}
        </p>
        <p className={`text-${color}-700`}>
          Exception Expiration Date:{" "}
          <span className="font-medium">
            {latest.expirationDate
              ? <>{fmt(latest.expirationDate)}{expired && <span className="ml-2 font-semibold text-red-700">(EXPIRED — source re-blocked)</span>}</>
              : "Not scheduled"}
          </span>
        </p>
        {latest.approverName && (
          <p className={`text-${color}-700`}>
            Approved By: <span className="font-medium">{latest.approverName}</span>
            {latest.approverTitle ? ` (${latest.approverTitle})` : ""}
          </p>
        )}
        {justification && <p className={`text-${color}-700`}>Reason: <span className="italic">{justification}</span></p>}
      </div>
    );
  }

  if (dt === "reverification_required" || dt === "remediation_required") {
    return (
      <div className="rounded border border-orange-300 bg-orange-50 px-3 py-2.5 text-xs space-y-1">
        <p className="font-semibold text-orange-800 flex items-center gap-1.5">
          <Wrench className="w-3.5 h-3.5" aria-hidden="true" />Compliance Decision: Fix Required
        </p>
        {latest.justification && <p className="text-orange-700">Reason: <span className="italic">{latest.justification}</span></p>}
        <p className="text-orange-600 text-[10px]">Recorded {fmt(latest.createdAt)} by {latest.createdByEmail}</p>
      </div>
    );
  }

  if (dt === "blocked" || dt === "keep_blocked") {
    return (
      <div className="rounded border border-muted bg-muted/40 px-3 py-2.5 text-xs space-y-1">
        <p className="font-semibold flex items-center gap-1.5">
          <ShieldOff className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
          Compliance Decision: Kept Blocked
        </p>
        {latest.justification && (
          <p className="text-muted-foreground">Reason: <span className="italic">{latest.justification}</span></p>
        )}
      </div>
    );
  }

  return null;
}

// ── Decision dialogs ──────────────────────────────────────────────────────────
function FixRequiredDialog({
  open, onClose, onSubmit, submitting,
}: { open: boolean; onClose: () => void; onSubmit: (d: { justification: string }) => void; submitting: boolean; }) {
  const [justification, setJustification] = useState("");
  const canSubmit = justification.trim().length >= 10;
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-orange-600" aria-hidden="true" />Fix Required
          </DialogTitle>
          <DialogDescription>
            The source will remain blocked from chatbot retrieval until issues are fixed and re-verified.
          </DialogDescription>
        </DialogHeader>
        <div className="py-1">
          <Label htmlFor="fix-reason" className="text-xs font-medium">
            Reason <span className="text-red-500">*</span>
          </Label>
          <Textarea id="fix-reason" className="mt-1 text-xs" rows={3}
            placeholder="Describe what needs to be fixed before this source can be used (min. 10 characters)…"
            value={justification} onChange={e => setJustification(e.target.value)} maxLength={2000} />
          <p className="text-xs text-muted-foreground mt-0.5">{justification.length}/2000</p>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="destructive" size="sm"
            onClick={() => onSubmit({ justification })} disabled={!canSubmit || submitting}>
            {submitting ? "Saving…" : "Mark Fix Required"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcceptRiskDialog({
  open, onClose, onSubmit, submitting,
}: {
  open: boolean; onClose: () => void;
  onSubmit: (d: { justification: string; businessReason: string; reviewerName: string; evidenceRef: string; reviewDate: string; validUntil: string }) => void;
  submitting: boolean;
}) {
  const { displayName } = useCurrentUserInfo();
  const today = new Date().toISOString().slice(0, 10);
  const [justification,  setJustification]  = useState("");
  const [businessReason, setBusinessReason] = useState("");
  const [reviewerName,   setReviewerName]   = useState(displayName);
  const [evidenceRef,    setEvidenceRef]    = useState("");
  const [reviewDate,     setReviewDate]     = useState("");
  const [validUntil,     setValidUntil]     = useState("");
  const [reviewDateErr,  setReviewDateErr]  = useState("");
  const [validUntilErr,  setValidUntilErr]  = useState("");

  const validateReviewDate = (v: string) => {
    if (!v) { setReviewDateErr("Risk Review Date is required."); return false; }
    if (v < today) { setReviewDateErr("Review date cannot be in the past."); return false; }
    setReviewDateErr(""); return true;
  };
  const validateValidUntil = (v: string) => {
    if (!v) { setValidUntilErr("Risk Acceptance Valid Until is required."); return false; }
    if (v < today) { setValidUntilErr("Valid until date must be a future date."); return false; }
    setValidUntilErr(""); return true;
  };
  const canSubmit = justification.trim().length >= 10 && businessReason.trim().length >= 5
    && reviewerName.trim().length > 0 && reviewDate.trim().length > 0 && validUntil.trim().length > 0
    && !reviewDateErr && !validUntilErr;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-600" aria-hidden="true" />Accept Risk
          </DialogTitle>
          <DialogDescription>
            The source will be unblocked and allowed for chatbot retrieval. This decision is audited and must be documented.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            <strong>Warning:</strong> Accepting risk means this source bypasses the FERPA security check.
            You must document the justification and business reason.
          </div>
          <div>
            <Label htmlFor="ar-just" className="text-xs font-medium">Risk Justification <span className="text-red-500">*</span></Label>
            <Textarea id="ar-just" className="mt-1 text-xs" rows={2}
              placeholder="Why is accepting this risk acceptable? (min. 10 characters)"
              value={justification} onChange={e => setJustification(e.target.value)} maxLength={2000} />
          </div>
          <div>
            <Label htmlFor="ar-business" className="text-xs font-medium">Business Reason <span className="text-red-500">*</span></Label>
            <Textarea id="ar-business" className="mt-1 text-xs" rows={2}
              placeholder="Describe the business context requiring this source (min. 5 characters)"
              value={businessReason} onChange={e => setBusinessReason(e.target.value)} maxLength={2000} />
          </div>
          <div>
            <Label htmlFor="ar-reviewer" className="text-xs font-medium">Reviewer Name <span className="text-red-500">*</span></Label>
            <Input id="ar-reviewer" className="mt-1 h-8 text-xs" placeholder="Full name of the approving reviewer"
              value={reviewerName} onChange={e => setReviewerName(e.target.value)} maxLength={200} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ar-review-date" className="text-xs font-medium">Risk Review Date <span className="text-red-500">*</span></Label>
              <Input id="ar-review-date" type="date" min={today}
                className={`mt-1 h-8 text-xs ${reviewDateErr ? "border-red-400" : ""}`}
                value={reviewDate}
                onChange={e => { setReviewDate(e.target.value); validateReviewDate(e.target.value); }} />
              {reviewDateErr && <p className="text-xs text-red-600 mt-0.5">{reviewDateErr}</p>}
            </div>
            <div>
              <Label htmlFor="ar-valid-until" className="text-xs font-medium">Risk Acceptance Valid Until <span className="text-red-500">*</span></Label>
              <Input id="ar-valid-until" type="date" min={today}
                className={`mt-1 h-8 text-xs ${validUntilErr ? "border-red-400" : ""}`}
                value={validUntil}
                onChange={e => { setValidUntil(e.target.value); validateValidUntil(e.target.value); }} />
              {validUntilErr && <p className="text-xs text-red-600 mt-0.5">{validUntilErr}</p>}
            </div>
          </div>
          <div>
            <Label htmlFor="ar-evidence" className="text-xs font-medium text-muted-foreground">Evidence Reference (optional)</Label>
            <Input id="ar-evidence" className="mt-1 h-8 text-xs font-mono"
              placeholder="Link or reference to supporting evidence"
              value={evidenceRef} onChange={e => setEvidenceRef(e.target.value)} maxLength={500} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
            onClick={() => {
              const rdOk = validateReviewDate(reviewDate);
              const vuOk = validateValidUntil(validUntil);
              if (rdOk && vuOk) onSubmit({ justification, businessReason, reviewerName, evidenceRef, reviewDate, validUntil });
            }} disabled={!canSubmit || submitting}>
            {submitting ? "Saving…" : "Accept Risk"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExceptionDialog({
  open, onClose, onSubmit, submitting,
}: {
  open: boolean; onClose: () => void;
  onSubmit: (d: { justification: string; approverName: string; approverTitle: string; approvalDate: string; expirationDate: string }) => void;
  submitting: boolean;
}) {
  const { displayName } = useCurrentUserInfo();
  const [justification, setJustification] = useState("");
  const [approvalJustification, setApprovalJustification] = useState("");
  const [approverName,  setApproverName]  = useState(displayName);
  const [approverTitle, setApproverTitle] = useState("");
  const [approvalDate,  setApprovalDate]  = useState(new Date().toISOString().slice(0, 10));
  const [expirationDate, setExpirationDate] = useState("");
  const [expiryErr, setExpiryErr] = useState("");

  const validateExpiry = (v: string) => {
    if (!v) { setExpiryErr("Expiration date is required."); return; }
    if (new Date(v) <= new Date()) { setExpiryErr("Expiration date must be in the future."); return; }
    setExpiryErr("");
  };
  const canSubmit = justification.trim().length >= 10 && approvalJustification.trim().length >= 10
    && approverName.trim().length > 0 && expirationDate.trim().length > 0 && !expiryErr;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-purple-600" aria-hidden="true" />Approve Exception
          </DialogTitle>
          <DialogDescription>
            A formal exception grants this source access despite FERPA check failures. Full audit trail is recorded
            and the exception auto-expires on the set date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs font-medium">Exception Reason <span className="text-red-500">*</span></Label>
            <Textarea className="mt-1 text-xs" rows={2}
              placeholder="Why is an exception required? (min. 10 characters)"
              value={justification} onChange={e => setJustification(e.target.value)} maxLength={2000} />
          </div>
          <div>
            <Label className="text-xs font-medium">Approval Justification <span className="text-red-500">*</span></Label>
            <Textarea className="mt-1 text-xs" rows={2}
              placeholder="Describe mitigating controls or compensating measures (min. 10 characters)"
              value={approvalJustification} onChange={e => setApprovalJustification(e.target.value)} maxLength={2000} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">Reviewer Name <span className="text-red-500">*</span></Label>
              <Input className="mt-1 h-8 text-xs" placeholder="Full name"
                value={approverName} onChange={e => setApproverName(e.target.value)} maxLength={200} />
            </div>
            <div>
              <Label className="text-xs font-medium">Reviewer Title</Label>
              <Input className="mt-1 h-8 text-xs" placeholder="e.g. Privacy Officer"
                value={approverTitle} onChange={e => setApproverTitle(e.target.value)} maxLength={200} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">Approval Date</Label>
              <Input type="date" className="mt-1 h-8 text-xs"
                value={approvalDate} onChange={e => setApprovalDate(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs font-medium">Expiration Date <span className="text-red-500">*</span></Label>
              <Input type="date" className={`mt-1 h-8 text-xs ${expiryErr ? "border-red-400" : ""}`}
                value={expirationDate} min={new Date().toISOString().slice(0, 10)}
                onChange={e => { setExpirationDate(e.target.value); validateExpiry(e.target.value); }} />
              {expiryErr && <p className="text-xs text-red-600 mt-0.5">{expiryErr}</p>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700"
            onClick={() => onSubmit({
              justification: `${justification}\n\nApproval justification: ${approvalJustification}`,
              approverName, approverTitle, approvalDate, expirationDate,
            })} disabled={!canSubmit || submitting}>
            {submitting ? "Saving…" : "Approve Exception"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeepBlockedDialog({
  open, onClose, onSubmit, submitting,
}: { open: boolean; onClose: () => void; onSubmit: (d: { justification: string }) => void; submitting: boolean; }) {
  const [justification, setJustification] = useState("");
  const canSubmit = justification.trim().length >= 5;
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="w-4 h-4 text-muted-foreground" aria-hidden="true" />Keep Blocked
          </DialogTitle>
          <DialogDescription>
            This source will remain excluded from all chatbot knowledge retrieval.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs font-medium">Reason <span className="text-red-500">*</span></Label>
          <Textarea className="mt-1 text-xs" rows={3}
            placeholder="Why is this source being kept blocked? (min. 5 characters)"
            value={justification} onChange={e => setJustification(e.target.value)} maxLength={2000} />
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="secondary" size="sm"
            onClick={() => onSubmit({ justification })} disabled={!canSubmit || submitting}>
            {submitting ? "Saving…" : "Confirm Keep Blocked"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Decision panel (blocked / risk-accepted sources) ─────────────────────────
function DecisionPanel({
  verification, canManage, onDecisionMade, onReverify, reverifying,
}: {
  verification: FerpaVerification; canManage: boolean; onDecisionMade: () => void;
  onReverify: (sourceId: string) => void; reverifying: boolean;
}) {
  const authFetch = useFerpaFetch();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [modal, setModal] = useState<DecisionModal>(null);

  const failedAuto  = verification.controlResults.filter(
    r => r.verificationMethod === "automatic" && r.complianceStatus === "failed",
  );
  const needEvidence = verification.controlResults.filter(
    r => r.complianceStatus === "manual_required",
  );

  const decisionM = useMutation({
    mutationFn: async (payload: Record<string, string | undefined>) => {
      const r = await authFetch(
        `/api/organization/compliance/ferpa/source-verifications/${verification.id}/decisions`,
        { method: "POST", body: JSON.stringify(payload) },
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error((e as any).message ?? "Decision failed");
      }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Decision recorded." });
      qc.invalidateQueries({ queryKey: ["ferpa-source-verifications"] });
      qc.invalidateQueries({ queryKey: ["ferpa-decisions", verification.id] });
      setModal(null);
      onDecisionMade();
    },
    onError: (e: any) => toast({ title: e?.message ?? "Unable to save decision.", variant: "destructive" }),
  });

  const submit = (decisionType: string, extra: Record<string, string | undefined> = {}) =>
    decisionM.mutate({ decisionType, ...extra });

  const isBlocked = verification.overallStatus === "blocked";
  const isAllowed = verification.overallStatus === "allowed";

  return (
    <div className="space-y-3">
      {/* FERPA Status */}
      <div className="rounded-md border border-purple-100 bg-purple-50/20 px-3 py-2.5 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">FERPA Status</span>
          {isBlocked && <Badge variant="destructive" className="text-xs">🔴 Blocked</Badge>}
          {isAllowed && <Badge className="text-xs bg-amber-600 hover:bg-amber-600">🟡 Allowed</Badge>}
        </div>

        {isBlocked && (failedAuto.length > 0 || needEvidence.length > 0) && (
          <div className="text-xs text-muted-foreground space-y-0.5">
            <p className="font-medium text-foreground text-xs">Reason:</p>
            {failedAuto.length > 0 && (
              <p className="text-red-700">
                {failedAuto.length} failed security control{failedAuto.length !== 1 ? "s" : ""}
              </p>
            )}
            {needEvidence.length > 0 && (
              <p className="text-amber-700">
                {needEvidence.length} control{needEvidence.length !== 1 ? "s" : ""} require evidence
              </p>
            )}
          </div>
        )}

        {isBlocked && failedAuto.length > 0 && (
          <div className="text-xs space-y-1">
            <p className="font-medium">Failed Controls:</p>
            <div className="space-y-1">
              {failedAuto.map(fc => (
                <div key={fc.controlKey} className="rounded bg-red-50 border border-red-200 px-2 py-1.5">
                  <p className="font-medium text-red-800">— {fc.controlName}</p>
                  {fc.findings && <p className="text-red-600 text-[10px]">Reason: {fc.findings}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-xs font-medium mb-1">Compliance Decision:</p>
          <CurrentDecisionPanel verificationId={verification.id} />
          <_FerpaNullDecisionPlaceholder verificationId={verification.id} />
        </div>
      </div>

      {/* Actions */}
      {canManage && isBlocked && (
        <div className="rounded-md border border-dashed px-3 py-2.5">
          <p className="text-xs font-semibold mb-2">Available Actions:</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs border-orange-400 text-orange-700 hover:bg-orange-50"
              onClick={() => setModal("fix_required")}>
              <Wrench className="w-3 h-3 mr-1" /> Fix Required
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
              onClick={() => setModal("accept_risk")}>
              <ShieldAlert className="w-3 h-3 mr-1" /> Accept Risk
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-purple-400 text-purple-700 hover:bg-purple-50"
              onClick={() => setModal("exception_approved")}>
              <ShieldCheck className="w-3 h-3 mr-1" /> Approve Exception
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => setModal("keep_blocked")}>
              <ShieldOff className="w-3 h-3 mr-1" /> Keep Blocked
            </Button>
          </div>
        </div>
      )}

      {canManage && isAllowed && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-2">
          <p><strong>Risk accepted / Exception approved.</strong> This source is allowed for chatbot retrieval despite failing FERPA checks. Re-verify or change this decision to re-block.</p>
          <Button size="sm" variant="outline" className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
            onClick={() => onReverify(verification.knowledgeSourceId)} disabled={reverifying}>
            <RefreshCcw className={`w-3 h-3 mr-1 ${reverifying ? "animate-spin" : ""}`} />
            {reverifying ? "Re-verifying…" : "Re-verify"}
          </Button>
        </div>
      )}

      {/* Dialogs */}
      <FixRequiredDialog open={modal === "fix_required"} onClose={() => setModal(null)}
        submitting={decisionM.isPending}
        onSubmit={({ justification }) => submit("reverification_required", { justification })} />
      <AcceptRiskDialog open={modal === "accept_risk"} onClose={() => setModal(null)}
        submitting={decisionM.isPending}
        onSubmit={({ justification, businessReason, reviewerName, evidenceRef, reviewDate, validUntil }) =>
          submit("risk_accepted", {
            justification: JSON.stringify({ justification, businessReason, evidenceRef, reviewDate, validUntil }),
            approverName: reviewerName, expirationDate: validUntil,
          })} />
      <ExceptionDialog open={modal === "exception_approved"} onClose={() => setModal(null)}
        submitting={decisionM.isPending}
        onSubmit={({ justification, approverName, approverTitle, approvalDate, expirationDate }) =>
          submit("exception_approved", { justification, approverName, approverTitle, approvalDate, expirationDate })} />
      <KeepBlockedDialog open={modal === "keep_blocked"} onClose={() => setModal(null)}
        submitting={decisionM.isPending}
        onSubmit={({ justification }) => submit("blocked", { justification })} />
    </div>
  );
}

function _FerpaNullDecisionPlaceholder({ verificationId }: { verificationId: string }) {
  const authFetch = useFerpaFetch();
  const { data } = useQuery<FerpaDecision[]>({
    queryKey: ["ferpa-decisions", verificationId],
    queryFn: async () => {
      const r = await authFetch(
        `/api/organization/compliance/ferpa/source-verifications/${verificationId}/decisions`,
      );
      if (!r.ok) return [];
      return r.json();
    },
    staleTime: 10_000,
  });
  if (!data || data.length > 0) return null;
  return <p className="text-xs text-muted-foreground italic">No decision taken yet.</p>;
}

// ── FERPA Verification Details Modal ──────────────────────────────────────────
function FerpaVerificationModal({
  v, open, onClose, canManage, onRefresh,
}: {
  v: FerpaVerification; open: boolean; onClose: () => void;
  canManage: boolean; onRefresh: () => void;
}) {
  const showDecisionPanel = v.overallStatus === "blocked" || v.overallStatus === "allowed";

  const sorted = [...v.controlResults].sort((a, b) => {
    const rank = (s: string) => s === "failed" ? 0 : s === "manual_required" ? 1 : s === "passed" ? 2 : 3;
    return rank(a.complianceStatus) - rank(b.complianceStatus);
  });

  const autoControls   = sorted.filter(r => r.verificationMethod === "automatic");
  const manualControls = sorted.filter(r => r.verificationMethod === "manual_required");

  return (
    <Dialog open={open} onOpenChange={v2 => { if (!v2) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <GraduationCap className="w-4 h-4 text-purple-600" aria-hidden="true" />
            FERPA Verification Details
          </DialogTitle>
          {/* "name — status" subtitle, e.g. "www.sunymaritime.edu — blocked" */}
          <DialogDescription className="flex items-center gap-1.5 flex-wrap text-xs">
            <span className="font-medium text-foreground truncate max-w-xs">{v.sourceName}</span>
            <span className="text-muted-foreground">—</span>
            {statusBadge(v.overallStatus)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Source info — HIPAA-style labeled field block */}
          <div className="rounded-md border bg-muted/20 px-3 py-2.5 text-xs space-y-1.5">
            <p className="font-bold text-sm text-foreground">FERPA Compliance Details</p>
            <div className="space-y-1">
              <div className="flex items-baseline gap-1.5">
                <span className="font-medium text-foreground whitespace-nowrap min-w-[120px]">Knowledge Source:</span>
                <span className="text-muted-foreground break-words">{v.sourceName}</span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-medium text-foreground whitespace-nowrap min-w-[120px]">URL:</span>
                <span className="text-muted-foreground break-all">{v.sourceName || "—"}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-foreground whitespace-nowrap min-w-[120px]">Overall Status:</span>
                {statusBadge(v.overallStatus)}
              </div>
            </div>
          </div>

          {/* Summary section */}
          <div className="rounded-md border bg-white px-3 py-2.5 space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Summary</p>
            <div className="flex items-center gap-5 text-xs flex-wrap">
              <span className="flex items-center gap-1.5 text-green-700">
                <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                <strong>{v.passedCount}</strong> passed
              </span>
              <span className="flex items-center gap-1.5 text-red-600">
                <XCircle className="w-3.5 h-3.5" aria-hidden="true" />
                <strong>{v.failedCount}</strong> failed
              </span>
              <span className="flex items-center gap-1.5 text-amber-600">
                <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                <strong>{v.manualRequiredCount}</strong> need evidence
              </span>
            </div>
          </div>

          {/* Status message banner — exact wording per spec */}
          {v.overallStatus === "blocked" && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-800 flex items-start gap-2">
              <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                <strong>Source is blocked from chatbot retrieval.</strong>{" "}
                Failed checks must be resolved before the source becomes available.
              </span>
            </div>
          )}
          {v.overallStatus === "active" && (
            <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2.5 text-xs text-green-800 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                <strong>Source passed FERPA verification</strong> and is available for chatbot retrieval.
              </span>
            </div>
          )}
          {v.overallStatus === "allowed" && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                <strong>Risk has been accepted.</strong>{" "}
                Source availability depends on the approved compliance decision.
              </span>
            </div>
          )}
          {(v.overallStatus === "pending_review" || v.overallStatus === "pending_verification") && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-700 flex items-start gap-2">
              <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span><strong>Verification is pending completion.</strong></span>
            </div>
          )}

          {/* Decision panel (blocked / risk-accepted sources) */}
          {showDecisionPanel && (
            <DecisionPanel verification={v} canManage={canManage}
              onDecisionMade={onRefresh}
              onReverify={() => {}} reverifying={false} />
          )}

          {/* FERPA Control Results — all controls sorted fail → pending → passed */}
          {sorted.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <ShieldAlert className="w-3 h-3" aria-hidden="true" /> FERPA Control Results
              </p>
              {sorted.map(r => (
                <ControlComplianceCard key={r.id} r={r}
                  sourceId={v.knowledgeSourceId}
                  canManage={canManage} onEvidenceSaved={onRefresh} />
              ))}
            </div>
          )}

          {/* Decision history */}
          <DecisionHistoryPanel verificationId={v.id} />
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete confirmation dialog ────────────────────────────────────────────────
function DeleteVerificationDialog({
  open, onClose, sourceName, onConfirm, deleting,
}: {
  open: boolean; onClose: () => void; sourceName: string;
  onConfirm: () => void; deleting: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            Delete FERPA Verification Record
          </DialogTitle>
          <DialogDescription>
            This will permanently delete the FERPA verification record for{" "}
            <strong className="text-foreground">{sourceName}</strong>, including all control
            results, evidence, and decision history. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />
          The knowledge source itself will not be deleted — only the FERPA compliance record.
          Re-adding or re-verifying the source will create a new verification.
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={onConfirm} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Individual source card — inline expand/collapse (HIPAA pattern) ───────────
function SourceCard({
  v, canManage, onReverify, reverifying, onRefresh,
}: {
  v: FerpaVerification; canManage: boolean;
  onReverify: (id: string) => void; reverifying: boolean; onRefresh: () => void;
}) {
  const [expanded,   setExpanded]   = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const authFetch = useFerpaFetch();
  const { toast } = useToast();
  const qc = useQueryClient();

  const showDecisionPanel = v.overallStatus === "blocked" || v.overallStatus === "allowed";

  const sorted = [...v.controlResults].sort((a, b) => {
    const rank = (s: string) => s === "failed" ? 0 : s === "manual_required" ? 1 : s === "passed" ? 2 : 3;
    return rank(a.complianceStatus) - rank(b.complianceStatus);
  });

  const deleteM = useMutation({
    mutationFn: () =>
      authFetch(
        `/api/organization/compliance/ferpa/source-verifications/${v.knowledgeSourceId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      toast({ title: "FERPA verification record deleted." });
      setDeleteOpen(false);
      qc.invalidateQueries({ queryKey: ["ferpa-source-verifications"] });
      onRefresh();
    },
    onError: () => toast({ title: "Failed to delete record.", variant: "destructive" }),
  });

  return (
    <>
      <div className="border rounded-lg overflow-hidden ml-2 bg-white">
        {/* Clickable header row — entire row toggles inline expansion (HIPAA pattern) */}
        <div
          className="flex items-start gap-3 px-3 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={() => setExpanded(e => !e)}
          role="button"
          aria-expanded={expanded}
        >
          <div className="flex-1 min-w-0 space-y-1">
            {/* Title row */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm truncate max-w-xs">{v.sourceName}</span>
              <Badge variant="outline" className="text-[10px] h-4 px-1 capitalize">{v.sourceType}</Badge>
              {statusBadge(v.overallStatus)}
            </div>
            {/* URL row */}
            {v.sourceType === "url" && v.sourceName && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                <ExternalLink className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
                <span className="truncate">{v.sourceName}</span>
              </p>
            )}
            {/* Stats row */}
            <div className="flex items-center gap-3 text-xs flex-wrap text-muted-foreground">
              {v.passedCount > 0 && (
                <span className="flex items-center gap-1 text-green-700">
                  <CheckCircle2 className="w-3 h-3" aria-hidden="true" />{v.passedCount} passed
                </span>
              )}
              {v.failedCount > 0 && (
                <span className="flex items-center gap-1 text-red-600 font-medium">
                  <XCircle className="w-3 h-3" aria-hidden="true" />{v.failedCount} failed
                </span>
              )}
              {v.manualRequiredCount > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Clock className="w-3 h-3" aria-hidden="true" />{v.manualRequiredCount} need evidence
                </span>
              )}
              {v.verificationCompletedAt && (
                <span className="text-muted-foreground/70">
                  Last verified {new Date(v.verificationCompletedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
            {canManage && (v.overallStatus === "blocked" || v.overallStatus === "pending_review") && (
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={e => { e.stopPropagation(); onReverify(v.knowledgeSourceId); }}
                disabled={reverifying}>
                <RefreshCcw className={`w-3 h-3 mr-1 ${reverifying ? "animate-spin" : ""}`} aria-hidden="true" />
                {reverifying ? "Verifying…" : "Re-verify"}
              </Button>
            )}
            {expanded
              ? <ChevronUp   className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
              : <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />}
          </div>
        </div>

        {/* Inline expanded detail — HIPAA style */}
        {expanded && (
          <div className="border-t px-3 py-3 space-y-4 bg-muted/10">
            {/* FERPA Compliance Details — labeled field block */}
            <div className="rounded-md border bg-white px-3 py-2.5 text-xs space-y-1.5">
              <p className="font-bold text-sm text-foreground">FERPA Compliance Details</p>
              <div className="space-y-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-medium text-foreground whitespace-nowrap min-w-[120px]">Knowledge Source:</span>
                  <span className="text-muted-foreground break-words">{v.sourceName}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-foreground whitespace-nowrap min-w-[120px]">FERPA Status:</span>
                  {statusBadge(v.overallStatus)}
                </div>
                {(v.failedCount > 0 || v.manualRequiredCount > 0) && (
                  <div className="flex items-start gap-1.5">
                    <span className="font-medium text-foreground whitespace-nowrap min-w-[120px]">Reason:</span>
                    <div className="text-muted-foreground space-y-0.5">
                      {v.failedCount > 0 && (
                        <p>{v.failedCount} failed {v.failedCount === 1 ? "security control" : "security controls"}</p>
                      )}
                      {v.manualRequiredCount > 0 && (
                        <p>{v.manualRequiredCount} {v.manualRequiredCount === 1 ? "control requires" : "controls require"} evidence</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status banner */}
            {v.overallStatus === "blocked" && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-800 flex items-start gap-2">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  <strong>Source is blocked from chatbot retrieval.</strong>{" "}
                  Failed checks must be resolved before the source becomes available.
                </span>
              </div>
            )}
            {v.overallStatus === "active" && (
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2.5 text-xs text-green-800 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  <strong>Source passed FERPA verification</strong> and is available for chatbot retrieval.
                </span>
              </div>
            )}
            {v.overallStatus === "allowed" && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>
                  <strong>Risk has been accepted.</strong>{" "}
                  Source availability depends on the approved compliance decision.
                </span>
              </div>
            )}
            {(v.overallStatus === "pending_review" || v.overallStatus === "pending_verification") && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-700 flex items-start gap-2">
                <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span><strong>Verification is pending completion.</strong></span>
              </div>
            )}

            {/* Decision panel (blocked / risk-accepted) */}
            {showDecisionPanel && (
              <DecisionPanel verification={v} canManage={canManage}
                onDecisionMade={onRefresh}
                onReverify={() => onReverify(v.knowledgeSourceId)}
                reverifying={reverifying} />
            )}

            {/* FERPA Control Results */}
            {sorted.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <ShieldAlert className="w-3 h-3" aria-hidden="true" /> FERPA Control Results
                </p>
                {sorted.map(r => (
                  <ControlComplianceCard key={r.id} r={r}
                    sourceId={v.knowledgeSourceId}
                    canManage={canManage} onEvidenceSaved={onRefresh} />
                ))}
              </div>
            )}

            {/* Decision history */}
            <DecisionHistoryPanel verificationId={v.id} />

            {/* Delete record — bottom of expanded area */}
            {canManage && (
              <div className="flex justify-end pt-1 border-t">
                <Button size="sm" variant="outline"
                  className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={() => setDeleteOpen(true)} disabled={deleteM.isPending}>
                  <Trash2 className="w-3 h-3" aria-hidden="true" />Delete Record
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <DeleteVerificationDialog
        open={deleteOpen} onClose={() => setDeleteOpen(false)}
        sourceName={v.sourceName}
        onConfirm={() => deleteM.mutate()}
        deleting={deleteM.isPending}
      />
    </>
  );
}

// ── Controls overview (FERPA Control Modules tab) — category-tab architecture ──
function ControlModulesTab({ sources, canManage, onRefresh }: {
  sources: FerpaVerification[]; canManage: boolean; onRefresh: () => void;
}) {
  type CatControl = { v: FerpaVerification; r: FerpaControlResult };

  // Group all control results by category.
  // Resolution order:
  // 1. linkedEntityType → FERPA_ENTITY_TYPE_LABELS label (or raw type string for unknown types)
  // 2. controlKey → FERPA_CONTROL_KEY_CATEGORY (covers automatic controls whose LinkedEntityType
  //    is null because the backend AutoLinkFerpaEntitiesAsync only links manual controls)
  // 3. controlName as last resort
  const byCategory = new Map<string, CatControl[]>();
  for (const v of sources) {
    for (const r of v.controlResults) {
      const cat = r.linkedEntityType
        ? (FERPA_ENTITY_TYPE_LABELS[r.linkedEntityType] ?? r.linkedEntityType)
        : (FERPA_CONTROL_KEY_CATEGORY[r.controlKey] ?? r.controlName ?? "General Controls");
      if (!byCategory.has(cat)) byCategory.set(cat, []);
      byCategory.get(cat)!.push({ v, r });
    }
  }

  // Sort categories: failures-first → pending → clean
  const categories = Array.from(byCategory.keys()).sort((a, b) => {
    const score = (cat: string) => {
      const cs = byCategory.get(cat)!;
      if (cs.some(c => c.r.complianceStatus === "failed"))          return 0;
      if (cs.some(c => c.r.complianceStatus === "manual_required")) return 1;
      return 2;
    };
    return score(a) - score(b);
  });

  const [activeCategory, setActiveCategory] = useState<string>(() => categories[0] ?? "");
  // Keep selection valid if sources change (e.g. after re-verify)
  const resolvedCat = categories.includes(activeCategory) ? activeCategory : (categories[0] ?? "");

  if (categories.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No FERPA controls found for this bot's sources.
      </p>
    );
  }

  // Controls in the active category, sorted fail → pending → passed
  const activeControls = [...(byCategory.get(resolvedCat) ?? [])].sort((a, b) => {
    const rank = (s: string) =>
      s === "failed" ? 0 : s === "manual_required" ? 1 : s === "passed" ? 2 : 3;
    return rank(a.r.complianceStatus) - rank(b.r.complianceStatus);
  });

  return (
    <div>
      {/* Horizontal scrollable category tab bar — HIPAA interaction pattern */}
      <div className="border-b overflow-x-auto">
        <div className="flex min-w-max" role="tablist" aria-label="FERPA control categories">
          {categories.map(cat => {
            const catControls = byCategory.get(cat)!;
            const hasFailed   = catControls.some(c => c.r.complianceStatus === "failed");
            const hasPending  = catControls.some(c => c.r.complianceStatus === "manual_required");
            const isActive    = cat === resolvedCat;

            // Status indicator dot colour
            const dotCls = hasFailed
              ? "bg-red-500"
              : hasPending
                ? "bg-amber-500"
                : "bg-green-500";

            return (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveCategory(cat)}
                className={[
                  "px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors",
                  "flex items-center gap-1.5",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-inset",
                  isActive
                    ? "border-purple-600 text-purple-700 bg-purple-50/40"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/40",
                ].join(" ")}
              >
                {/* Status dot */}
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotCls}`} aria-hidden="true" />

                {cat}

                {/* Count badge */}
                <span className={`text-[10px] font-normal rounded-full px-1.5 py-px ${
                  isActive ? "bg-purple-100 text-purple-700" : "bg-muted text-muted-foreground"
                }`}>
                  {catControls.length}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Control cards for the selected category */}
      <div className="space-y-2 p-3" role="tabpanel">
        {activeControls.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No controls in this category.</p>
        ) : (
          activeControls.map(({ v, r }) => (
            <ControlComplianceCard
              key={`${v.id}-${r.id}`}
              r={r}
              sourceId={v.knowledgeSourceId}
              canManage={canManage}
              onEvidenceSaved={onRefresh}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Verification details tab (inline detail for all sources) ──────────────────
function VerificationDetailsTab({ sources, canManage, onRefresh, onReverify, reverifyingId }: {
  sources: FerpaVerification[]; canManage: boolean; onRefresh: () => void;
  onReverify: (id: string) => void; reverifyingId: string | undefined;
}) {
  if (sources.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">No verifications for this bot.</p>;
  }

  return (
    <div className="space-y-4 px-4 pb-4">
      {sources.map(v => {
        const sorted = [...v.controlResults].sort((a, b) => {
          const rank = (s: string) => s === "failed" ? 0 : s === "manual_required" ? 1 : s === "passed" ? 2 : 3;
          return rank(a.complianceStatus) - rank(b.complianceStatus);
        });
        const autoControls   = sorted.filter(r => r.verificationMethod === "automatic");
        const manualControls = sorted.filter(r => r.verificationMethod === "manual_required");
        const showDecision   = v.overallStatus === "blocked" || v.overallStatus === "allowed";

        return (
          <div key={v.id} className="border rounded-lg bg-white overflow-hidden">
            {/* Source header — unified with SourceCard layout */}
            <div className="px-3 py-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1 min-w-0 flex-1">
                  {/* Title + badges */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate max-w-xs">{v.sourceName}</span>
                    <Badge variant="outline" className="text-[10px] h-4 px-1 capitalize">{v.sourceType}</Badge>
                    {statusBadge(v.overallStatus)}
                  </div>
                  {/* URL */}
                  {v.sourceType === "url" && v.sourceName && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                      <ExternalLink className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
                      <span className="truncate">{v.sourceName}</span>
                    </p>
                  )}
                  {/* Stats */}
                  <div className="flex items-center gap-3 text-xs flex-wrap text-muted-foreground">
                    {v.passedCount > 0 && (
                      <span className="flex items-center gap-1 text-green-700">
                        <CheckCircle2 className="w-3 h-3" />{v.passedCount} passed
                      </span>
                    )}
                    {v.failedCount > 0 && (
                      <span className="flex items-center gap-1 text-red-600 font-medium">
                        <XCircle className="w-3 h-3" />{v.failedCount} failed
                      </span>
                    )}
                    {v.manualRequiredCount > 0 && (
                      <span className="flex items-center gap-1 text-amber-600">
                        <Clock className="w-3 h-3" />{v.manualRequiredCount} need evidence
                      </span>
                    )}
                    {v.verificationCompletedAt && (
                      <span className="text-muted-foreground/70">
                        Last verified {new Date(v.verificationCompletedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 flex-shrink-0"
                    onClick={() => onReverify(v.knowledgeSourceId)}
                    disabled={reverifyingId === v.knowledgeSourceId}>
                    <RefreshCcw className={`w-3 h-3 ${reverifyingId === v.knowledgeSourceId ? "animate-spin" : ""}`} />
                    Re-verify
                  </Button>
                )}
              </div>
            </div>
            <div className="border-t" />

            <div className="px-3 py-3 space-y-3">
              {v.verificationSummary && (
                <p className="text-xs text-muted-foreground">{v.verificationSummary}</p>
              )}

              {showDecision && (
                <DecisionPanel verification={v} canManage={canManage}
                  onDecisionMade={onRefresh}
                  onReverify={onReverify} reverifying={reverifyingId === v.knowledgeSourceId} />
              )}

              {v.overallStatus === "pending_review" && (
                <div className="rounded bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-xs text-amber-800">
                  <strong>Evidence required.</strong> Supply evidence for the manual controls below.
                </div>
              )}

              {autoControls.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                    <ShieldAlert className="w-3 h-3" />Automatic Controls
                  </p>
                  <div className="space-y-1.5">
                    {autoControls.map(r => (
                      <ControlComplianceCard key={r.id} r={r}
                        sourceId={v.knowledgeSourceId}
                        canManage={canManage} onEvidenceSaved={onRefresh} />
                    ))}
                  </div>
                </div>
              )}

              {manualControls.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                    <ClipboardList className="w-3 h-3" />Manual Controls
                  </p>
                  <div className="space-y-1.5">
                    {manualControls.map(r => (
                      <ControlComplianceCard key={r.id} r={r}
                        sourceId={v.knowledgeSourceId}
                        canManage={canManage} onEvidenceSaved={onRefresh} />
                    ))}
                  </div>
                </div>
              )}

              <DecisionHistoryPanel verificationId={v.id} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Bot-level group card — flat tab bar matching HIPAA interaction pattern ─────
function BotGroupCard({
  botId, botName, sources, canManage, onReverify, reverifyingId, onRefresh,
}: {
  botId: string; botName: string; sources: FerpaVerification[];
  canManage: boolean; onReverify: (sourceId: string) => void;
  reverifyingId: string | undefined; onRefresh: () => void;
}) {
  const [expanded,   setExpanded]   = useState(false);
  // "Knowledge Sources" sentinel + any category name from data
  const KS_TAB = "__knowledge_sources__";
  const [activeTab, setActiveTab]   = useState(KS_TAB);

  // ── Compliance stats ────────────────────────────────────────────────────────
  const totalPassed   = sources.reduce((s, v) => s + v.passedCount,        0);
  const totalFailed   = sources.reduce((s, v) => s + v.failedCount,        0);
  const totalPending  = sources.reduce((s, v) => s + v.manualRequiredCount, 0);
  const riskAccepted  = sources.filter(v => v.overallStatus === "allowed").length;
  const blockedCount  = sources.filter(v => v.overallStatus === "blocked").length;
  const totalControls = totalPassed + totalFailed;
  const compliancePct = totalControls > 0 ? Math.round((totalPassed / totalControls) * 100) : 100;
  const pctColor      =
    compliancePct >= 90 ? "text-green-700" :
    compliancePct >= 70 ? "text-amber-600" : "text-red-600";

  // ── Derive control category tabs from source data ───────────────────────────
  // Resolution order:
  // 1. linkedEntityType → FERPA_ENTITY_TYPE_LABELS label (or raw type string for unknown types)
  // 2. controlKey → FERPA_CONTROL_KEY_CATEGORY (covers automatic controls whose LinkedEntityType
  //    is null because the backend AutoLinkFerpaEntitiesAsync only links manual controls)
  // 3. controlName as last resort
  type CatControl = { v: FerpaVerification; r: FerpaControlResult };
  const byCategory = new Map<string, CatControl[]>();
  for (const v of sources) {
    for (const r of v.controlResults) {
      const cat = r.linkedEntityType
        ? (FERPA_ENTITY_TYPE_LABELS[r.linkedEntityType] ?? r.linkedEntityType)
        : (FERPA_CONTROL_KEY_CATEGORY[r.controlKey] ?? r.controlName ?? "General Controls");
      if (!byCategory.has(cat)) byCategory.set(cat, []);
      byCategory.get(cat)!.push({ v, r });
    }
  }
  // Sort categories: failures first → pending → clean
  const categoryTabs = Array.from(byCategory.keys()).sort((a, b) => {
    const score = (cat: string) => {
      const cs = byCategory.get(cat)!;
      if (cs.some(c => c.r.complianceStatus === "failed"))          return 0;
      if (cs.some(c => c.r.complianceStatus === "manual_required")) return 1;
      return 2;
    };
    return score(a) - score(b);
  });

  // Keep selection valid if sources change
  const resolvedTab =
    activeTab === KS_TAB || byCategory.has(activeTab) ? activeTab : KS_TAB;

  // Controls for the active category tab (non-KS tabs)
  const activeCatControls = resolvedTab !== KS_TAB
    ? [...(byCategory.get(resolvedTab) ?? [])].sort((a, b) => {
        const rank = (s: string) =>
          s === "failed" ? 0 : s === "manual_required" ? 1 : s === "passed" ? 2 : 3;
        return rank(a.r.complianceStatus) - rank(b.r.complianceStatus);
      })
    : [];

  return (
    <div className="border rounded-lg overflow-hidden shadow-sm">
      {/* ── Bot header — clickable row ── */}
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-muted/40 transition-colors bg-slate-50"
        onClick={() => setExpanded(e => !e)}
        role="button"
        aria-expanded={expanded}
      >
        <div className="flex-1 min-w-0">
          {/* Bot name + summary badges */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <GraduationCap className="w-4 h-4 text-purple-600 flex-shrink-0" aria-hidden="true" />
            <span className="font-bold text-sm">{botName}</span>
            <Badge variant="outline" className="text-xs">
              {sources.length} Knowledge Source{sources.length !== 1 ? "s" : ""}
            </Badge>
            {blockedCount > 0 && (
              <Badge variant="destructive" className="text-xs">{blockedCount} blocked</Badge>
            )}
            {riskAccepted > 0 && (
              <Badge className="text-xs bg-amber-600 hover:bg-amber-600">{riskAccepted} risk accepted</Badge>
            )}
          </div>
          {/* Compliance stats */}
          <div className="flex items-center gap-4 text-xs flex-wrap">
            <span className="flex items-center gap-1 text-green-700">
              <CheckCircle2 className="w-3 h-3" aria-hidden="true" />{totalPassed} passed
            </span>
            {totalFailed > 0 && (
              <span className="flex items-center gap-1 text-red-600 font-medium">
                <XCircle className="w-3 h-3" aria-hidden="true" />{totalFailed} failed
              </span>
            )}
            {totalPending > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <Clock className="w-3 h-3" aria-hidden="true" />{totalPending} need evidence
              </span>
            )}
            <span className={`font-semibold ${pctColor}`}>{compliancePct}% compliant</span>
          </div>
        </div>
        <div className="flex-shrink-0 pt-0.5">
          {expanded
            ? <ChevronUp   className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
            : <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />}
        </div>
      </div>

      {/* ── Expanded content ── */}
      {expanded && (
        <div className="border-t bg-muted/5">
          {/* Flat scrollable tab bar: "Knowledge Sources" + one tab per control category */}
          <div className="border-b bg-white overflow-x-auto">
            <div className="flex min-w-max" role="tablist" aria-label="FERPA sections">
              {/* Knowledge Sources — always first */}
              <button
                type="button" role="tab"
                aria-selected={resolvedTab === KS_TAB}
                onClick={() => setActiveTab(KS_TAB)}
                className={[
                  "flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-inset",
                  resolvedTab === KS_TAB
                    ? "border-purple-600 text-purple-700"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/40",
                ].join(" ")}
              >
                <List className="w-3 h-3" aria-hidden="true" />
                Knowledge Sources
                <span className={`text-[10px] rounded-full px-1.5 py-px ${
                  resolvedTab === KS_TAB
                    ? "bg-purple-100 text-purple-700"
                    : "bg-muted text-muted-foreground"
                }`}>{sources.length}</span>
              </button>

              {/* One tab per FERPA control category */}
              {categoryTabs.map(cat => {
                const catControls = byCategory.get(cat)!;
                const hasFailed   = catControls.some(c => c.r.complianceStatus === "failed");
                const hasPending  = catControls.some(c => c.r.complianceStatus === "manual_required");
                const isActive    = resolvedTab === cat;
                const dotCls      = hasFailed ? "bg-red-500" : hasPending ? "bg-amber-500" : "bg-green-500";
                return (
                  <button
                    key={cat} type="button" role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTab(cat)}
                    className={[
                      "flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-inset",
                      isActive
                        ? "border-purple-600 text-purple-700"
                        : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/40",
                    ].join(" ")}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotCls}`} aria-hidden="true" />
                    {cat}
                    <span className={`text-[10px] rounded-full px-1.5 py-px ${
                      isActive
                        ? "bg-purple-100 text-purple-700"
                        : "bg-muted text-muted-foreground"
                    }`}>{catControls.length}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Knowledge Sources tab content — expandable source cards */}
          {resolvedTab === KS_TAB && (
            <div className="px-3 py-3 space-y-2">
              {sources.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No knowledge sources attached to this bot yet.
                </p>
              ) : sources.map(v => (
                <SourceCard key={v.id} v={v} canManage={canManage}
                  onReverify={onReverify}
                  reverifying={reverifyingId === v.knowledgeSourceId}
                  onRefresh={onRefresh} />
              ))}
            </div>
          )}

          {/* Category tab content — ControlComplianceCard per control */}
          {resolvedTab !== KS_TAB && (
            <div className="space-y-2 p-3" role="tabpanel">
              {activeCatControls.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No controls in this category.
                </p>
              ) : activeCatControls.map(({ v, r }) => (
                <ControlComplianceCard
                  key={`${v.id}-${r.id}`}
                  r={r}
                  sourceId={v.knowledgeSourceId}
                  canManage={canManage}
                  onEvidenceSaved={onRefresh}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sentinel for unresolved bot ownership ─────────────────────────────────────
export const FERPA_UNKNOWN_BOT_ID = "__ferpa_unknown__";

// ── Exported hook: bot-grouped FERPA verification data ────────────────────────
export function useFerpaVerificationsData() {
  const authFetch = useFerpaFetch();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/ferpa";

  // 1. Chatbot list
  const { data: chatbotsData, isLoading: botsLoading } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["chatbots-list-ferpa"],
    queryFn: async () => {
      const r = await authFetch("/api/chatbots");
      if (!r.ok) return [];
      const json = await r.json();
      return Array.isArray(json) ? json : (json.items ?? json.chatbots ?? []);
    },
    staleTime: 60_000,
  });

  // 2. FERPA verifications
  const { data: verifications, isLoading: verificationsLoading } = useQuery<FerpaVerification[]>({
    queryKey: ["ferpa-source-verifications"],
    queryFn: async () => {
      const r = await authFetch(`${BASE}/source-verifications`);
      if (!r.ok) throw new Error("Failed to load FERPA source verifications");
      return r.json();
    },
    staleTime: 10_000,
    refetchInterval: (query) => {
      const rows = query.state.data as FerpaVerification[] | undefined;
      return rows?.some(v => v.overallStatus === "pending_verification") ? 3000 : false;
    },
  });

  // 3. Knowledge source ownership map
  const botIds = (chatbotsData ?? []).map(b => b.id);
  const { data: ksOwnerMap, isLoading: ksLoading } = useQuery<Map<string, string>>({
    queryKey: ["ferpa-ks-ownership", botIds.slice().sort().join(",")],
    enabled: botIds.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        botIds.map(async (botId) => {
          try {
            const r = await authFetch(`/api/chatbots/${botId}/knowledge`);
            if (!r.ok) return [] as [string, string][];
            const json = await r.json();
            const items: { id: string }[] = Array.isArray(json) ? json : (json.items ?? []);
            return items.map(ks => [ks.id.toLowerCase(), botId.toLowerCase()] as [string, string]);
          } catch {
            return [] as [string, string][];
          }
        }),
      );
      return new Map<string, string>(results.flat());
    },
    staleTime: 30_000,
  });

  // Build name map
  const botNameMap = new Map<string, string>(
    (chatbotsData ?? []).map(b => [b.id.toLowerCase(), b.name]),
  );

  const refresh = () => qc.invalidateQueries({ queryKey: ["ferpa-source-verifications"] });

  const reverifyM = useMutation({
    mutationFn: (sourceId: string) =>
      authFetch(`${BASE}/source-verifications/${sourceId}/reverify`, { method: "POST" }),
    onSuccess: () => {
      toast({ title: "FERPA re-verification queued. The status will update shortly." });
      refresh();
    },
    onError: () => toast({ title: "Re-verification failed.", variant: "destructive" }),
  });

  // Group verifications strictly by KS ownership
  const botOrder: string[] = botIds.map(id => id.toLowerCase());
  const byBot = new Map<string, FerpaVerification[]>();
  for (const botId of botOrder) byBot.set(botId, []);

  for (const v of (verifications ?? [])) {
    const ksId = v.knowledgeSourceId?.toLowerCase() ?? "";
    const resolvedId = ksOwnerMap?.get(ksId);
    if (resolvedId && byBot.has(resolvedId)) {
      byBot.get(resolvedId)!.push(v);
    } else {
      if (!byBot.has(FERPA_UNKNOWN_BOT_ID)) {
        botOrder.push(FERPA_UNKNOWN_BOT_ID);
        byBot.set(FERPA_UNKNOWN_BOT_ID, []);
      }
      byBot.get(FERPA_UNKNOWN_BOT_ID)!.push(v);
    }
  }

  return {
    isLoading: verificationsLoading || botsLoading || (botIds.length > 0 && ksLoading),
    botOrder, byBot, botNameMap, reverifyM, refresh,
  };
}

// ── Main exported component ───────────────────────────────────────────────────
export function FerpaSourceVerifications({ canManage }: { canManage: boolean }) {
  const { isLoading, botOrder, byBot, botNameMap, reverifyM, refresh } = useFerpaVerificationsData();

  if (isLoading) return (
    <div className="space-y-2">
      {[0, 1, 2].map(i => <Skeleton key={i} className="h-20 w-full rounded-lg" />)}
    </div>
  );

  const allSources    = botOrder.flatMap(id => byBot.get(id) ?? []);
  if (allSources.length === 0) {
    return (
      <div className="rounded border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
        <GraduationCap className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
        <p className="font-medium">No FERPA source verifications yet.</p>
        <p className="mt-1 text-xs">
          When a knowledge source is added to a FERPA-enabled bot, compliance
          verification is triggered automatically.
        </p>
      </div>
    );
  }

  const blockedCount  = allSources.filter(v => v.overallStatus === "blocked").length;
  const allowedCount  = allSources.filter(v => v.overallStatus === "allowed").length;
  const reviewCount   = allSources.filter(v => v.overallStatus === "pending_review").length;
  const verifiedCount = allSources.filter(v => v.overallStatus === "active").length;

  // Filter out bots with no verifications
  const activeBotOrder = botOrder.filter(id => (byBot.get(id) ?? []).length > 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-purple-600" aria-hidden="true" />
              Knowledge Source FERPA Verifications
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Automatically verified when knowledge sources are added. Blocked sources are excluded
              from chatbot retrieval unless a compliance officer accepts the risk or approves an exception.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {verifiedCount > 0 && <Badge variant="default">{verifiedCount} verified</Badge>}
            {blockedCount  > 0 && <Badge variant="destructive">{blockedCount} blocked</Badge>}
            {allowedCount  > 0 && <Badge className="bg-amber-600 hover:bg-amber-600">{allowedCount} risk accepted</Badge>}
            {reviewCount   > 0 && <Badge variant="secondary">{reviewCount} need review</Badge>}
            <Badge variant="outline">{allSources.length} total</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {activeBotOrder.map(botId => (
          <BotGroupCard
            key={botId}
            botId={botId}
            botName={botNameMap.get(botId) ?? (botId === FERPA_UNKNOWN_BOT_ID ? "Unknown Bot" : botId)}
            sources={byBot.get(botId) ?? []}
            canManage={canManage}
            onReverify={id => reverifyM.mutate(id)}
            reverifyingId={reverifyM.isPending ? reverifyM.variables : undefined}
            onRefresh={refresh}
          />
        ))}
      </CardContent>
    </Card>
  );
}
